import jsPDF from "jspdf";
import {
  countRegistrationGuests,
  resolveInvoiceRegistrationFeeLineAmount,
  shouldUseLateFeesForRegistration,
  shouldWaiveMainGuestFeesForRegistration,
} from "./invoiceCalculations";
import { getSpecialEventGuestsForProgram } from "./specialEventGuestNames";
import { normalizeRegistrationGuests } from "./registrationGuests";

/** jsPDF built-in fonts (Helvetica) only support WinAnsi; UTF-8 needs embedded TTF. */
const INVOICE_FONT = "NotoSans";

const NOTO_SANS_TTF = {
  regular:
    "https://cdn.jsdelivr.net/gh/googlefonts/noto-fonts@main/hinted/ttf/NotoSans/NotoSans-Regular.ttf",
  bold: "https://cdn.jsdelivr.net/gh/googlefonts/noto-fonts@main/hinted/ttf/NotoSans/NotoSans-Bold.ttf",
  italic:
    "https://cdn.jsdelivr.net/gh/googlefonts/noto-fonts@main/hinted/ttf/NotoSans/NotoSans-Italic.ttf",
} as const;

let cachedNotoSansBase64: {
  regular: string;
  bold: string;
  italic: string;
} | null = null;

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

async function loadNotoSansBase64(): Promise<NonNullable<typeof cachedNotoSansBase64>> {
  if (cachedNotoSansBase64) {
    return cachedNotoSansBase64;
  }
  const [regRes, boldRes, italicRes] = await Promise.all([
    fetch(NOTO_SANS_TTF.regular),
    fetch(NOTO_SANS_TTF.bold),
    fetch(NOTO_SANS_TTF.italic),
  ]);
  if (!regRes.ok || !boldRes.ok || !italicRes.ok) {
    throw new Error("Could not load Noto Sans fonts for invoice PDF");
  }
  const [regular, bold, italic] = await Promise.all([
    regRes.arrayBuffer(),
    boldRes.arrayBuffer(),
    italicRes.arrayBuffer(),
  ]);
  cachedNotoSansBase64 = {
    regular: arrayBufferToBase64(regular),
    bold: arrayBufferToBase64(bold),
    italic: arrayBufferToBase64(italic),
  };
  return cachedNotoSansBase64;
}

function embedNotoSans(doc: jsPDF, data: NonNullable<typeof cachedNotoSansBase64>): void {
  doc.addFileToVFS("NotoSans-Regular.ttf", data.regular);
  doc.addFont("NotoSans-Regular.ttf", INVOICE_FONT, "normal");
  doc.addFileToVFS("NotoSans-Bold.ttf", data.bold);
  doc.addFont("NotoSans-Bold.ttf", INVOICE_FONT, "bold");
  doc.addFileToVFS("NotoSans-Italic.ttf", data.italic);
  doc.addFont("NotoSans-Italic.ttf", INVOICE_FONT, "italic");
}

/** Right edge of description column (before amount); lines wrap within maxWidth. */
const DESC_RIGHT_X = 445;
const DESC_MAX_WIDTH = 365;
const AMOUNT_X = 460;
const LINE_HEIGHT = 12;
const PAGE_HEIGHT = 792;

const INVOICE_BOX = {
  x: 70,
  width: 480,
  top: 275,
  minHeight: 140,
  headerHeight: 20,
  totalRowHeight: 20,
  eventTitleOffset: 40,
  itemsStartOffset: 63,
  itemsBottomPadding: 8,
  totalTextOffset: 14,
  amountDividerX: 450,
} as const;

type InvoicePDFLineItem = {
  description: string;
  amountText: string;
};

function getCurrencySymbols(currency: string): { positive: string; negative: string } {
  const isUsd = currency.toUpperCase() === "USD";
  return {
    positive: isUsd ? "$" : "EURO ",
    negative: isUsd ? "-$" : "-EURO ",
  };
}

function measureLineItemsHeight(
  doc: jsPDF,
  fontFamily: string,
  lineItems: InvoicePDFLineItem[],
  fontSize = 10,
): number {
  doc.setFont(fontFamily, "normal");
  doc.setFontSize(fontSize);
  return lineItems.reduce((height, item) => {
    const lines = doc.splitTextToSize(item.description, DESC_MAX_WIDTH);
    return height + Math.max(1, lines.length) * LINE_HEIGHT;
  }, 0);
}

function calculateInvoiceBoxLayout(
  doc: jsPDF,
  fontFamily: string,
  lineItems: InvoicePDFLineItem[],
): {
  boxHeight: number;
  boxBottom: number;
  totalBarY: number;
  totalTextY: number;
  itemsStartY: number;
  layoutShift: number;
} {
  const {
    top,
    minHeight,
    totalRowHeight,
    itemsStartOffset,
    itemsBottomPadding,
    totalTextOffset,
  } = INVOICE_BOX;

  const itemsHeight = measureLineItemsHeight(doc, fontFamily, lineItems);
  const contentEndOffset = itemsStartOffset + itemsHeight + itemsBottomPadding;
  const minContentEndOffset = minHeight - totalRowHeight;
  const totalBarY = top + Math.max(contentEndOffset, minContentEndOffset);
  const boxHeight = totalBarY + totalRowHeight - top;

  return {
    boxHeight,
    boxBottom: top + boxHeight,
    totalBarY,
    totalTextY: totalBarY + totalTextOffset,
    itemsStartY: top + itemsStartOffset,
    layoutShift: boxHeight - minHeight,
  };
}

function buildInvoicePDFLineItems(params: {
  person: InvoicePDFData["person"];
  regAmount: number;
  guestNames?: string[];
  /** @deprecated Prefer guestNames */
  guestName?: string;
  /** @deprecated Prefer guestNames */
  guestName2?: string;
  applicableGuestFee: number;
  program?: InvoicePDFData["event"]["program"];
  selectedSpecialEventIds?: string[];
  specialEventGuestNames?: Record<string, string | string[]>;
  eventTitle: string;
  additionalFeeValue: number;
  creditAmount: number;
  paidAmount: number;
  isPaid: boolean;
  isPrepaid?: boolean;
  currency: string;
}): InvoicePDFLineItem[] {
  const {
    person,
    regAmount,
    guestNames,
    guestName,
    guestName2,
    applicableGuestFee,
    program,
    selectedSpecialEventIds,
    specialEventGuestNames = {},
    eventTitle,
    additionalFeeValue,
    creditAmount,
    paidAmount,
    isPaid,
    isPrepaid = false,
    currency,
  } = params;

  const { positive: currencySymbol, negative: currencySymbolNeg } =
    getCurrencySymbols(currency);
  const items: InvoicePDFLineItem[] = [];
  const regName = person.name || "N/A";
  const registrationFeeLabel = isPrepaid
    ? `${regName} - Registration Fee: Prepaid`
    : `${regName} - Registration Fee`;

  items.push({
    description: registrationFeeLabel,
    amountText: `${currencySymbol}${regAmount.toFixed(2)}`,
  });

  const resolvedGuestNames =
    guestNames && guestNames.length > 0
      ? guestNames
      : [guestName, guestName2].filter(
          (n): n is string => typeof n === "string" && n.trim().length > 0,
        );

  for (const name of resolvedGuestNames) {
    items.push({
      description: `${name} - Guest Registration Fee`,
      amountText: `${currencySymbol}${applicableGuestFee.toFixed(2)}`,
    });
  }

  const regNameForSpecial = person.name || "N/A";
  const eventTitleForSpecial = eventTitle || "Event";
  const idsToRender = selectedSpecialEventIds ?? [];
  if (program?.length && idsToRender.length > 0) {
    const progsToRender = idsToRender
      .map((id) => program.find((p) => p.id === id))
      .filter(Boolean);

    for (const prog of progsToRender) {
      if (!prog) {
        continue;
      }
      const programFee = prog.fee ?? 0;
      const specialEventTitle =
        prog.registrationPageSessionTitle ||
        (eventTitleForSpecial && prog.title
          ? `${eventTitleForSpecial} - ${prog.title}`
          : prog.title) ||
        "Special Event";
      items.push({
        description: `${regNameForSpecial} - Event Fee: ${specialEventTitle}`,
        amountText: `${currencySymbol}${programFee.toFixed(2)}`,
      });
    }

    for (const prog of progsToRender) {
      if (!prog) {
        continue;
      }
      const key = (prog as { id?: string }).id ?? program.indexOf(prog);
      const programId = String(key);
      const guests = getSpecialEventGuestsForProgram(
        specialEventGuestNames,
        programId,
      );
      if (guests.length === 0) {
        continue;
      }
      const guestFeeAmount = prog.specialEventGuestFee ?? 0;
      const specialEventTitle =
        prog.registrationPageSessionTitle ||
        (eventTitleForSpecial && prog.title
          ? `${eventTitleForSpecial} - ${prog.title}`
          : prog.title) ||
        "Special Event";
      for (const specialGuestName of guests) {
        items.push({
          description: `${specialGuestName.trim()} - Special Event Guest Fee: ${specialEventTitle}`,
          amountText: `${currencySymbol}${guestFeeAmount.toFixed(2)}`,
        });
      }
    }
  }

  if (additionalFeeValue > 0) {
    items.push({
      description: "Additional Fee",
      amountText: `${currencySymbol}${additionalFeeValue.toFixed(2)}`,
    });
  }

  if (creditAmount > 0) {
    items.push({
      description: "Credit Applied",
      amountText: `${currencySymbolNeg}${creditAmount.toFixed(2)}`,
    });
  }

  if (!isPaid && paidAmount > 0) {
    items.push({
      description: "Amount Paid",
      amountText: `${currencySymbolNeg}${paidAmount.toFixed(2)}`,
    });
  }

  return items;
}

function buildCustomInvoicePDFLineItems(
  items: { description: string; amountText: string }[],
  params: {
    currency: string;
    paidAmount: number;
    isPaid: boolean;
    paidLabel?: string;
    creditAppliedDisplay?: number;
  },
): InvoicePDFLineItem[] {
  const result: InvoicePDFLineItem[] = [...items];
  const { negative: currencySymbolNeg } = getCurrencySymbols(params.currency);
  const creditApplied = Number(params.creditAppliedDisplay) || 0;
  if (creditApplied > 0) {
    result.push({
      description: params.paidLabel?.trim() || "Credit Balance Applied",
      amountText: `${currencySymbolNeg}${creditApplied.toFixed(2)}`,
    });
  } else if (!params.isPaid && params.paidAmount > 0) {
    result.push({
      description: params.paidLabel?.trim() || "Amount Paid",
      amountText: `${currencySymbolNeg}${params.paidAmount.toFixed(2)}`,
    });
  }
  return result;
}

function drawDescriptionAmountRow(
  doc: jsPDF,
  fontFamily: string,
  description: string,
  amountText: string,
  y: number,
  fontSize = 10
): number {
  doc.setFont(fontFamily, "normal");
  doc.setFontSize(fontSize);
  const lines = doc.splitTextToSize(description, DESC_MAX_WIDTH);
  lines.forEach((line: string, i: number) => {
    const w = doc.getTextWidth(line);
    doc.text(line, DESC_RIGHT_X - w, y + i * LINE_HEIGHT);
  });
  doc.text(amountText, AMOUNT_X, y);
  return y + Math.max(1, lines.length) * LINE_HEIGHT;
}

export interface InvoicePDFData {
  event: {
    id?: string;
    title?: string;
    startDate?: number;
    endDate?: number;
    location?: string;
    fee?: {
      fee?: number;
      memberFee?: number;
      guestFee?: number;
      guestLateFee?: number;
      lateFee?: number;
      lateFeeDate?: number;
      noRefundDate?: number;
      currency?: string;
    };
    program?: Array<{ id?: string; title?: string; registrationPageSessionTitle?: string; fee?: number; specialEventGuestFee?: number | null }>;
  };
  person: {
    name: string;
    email?: string;
  };
  firm?: {
    name: string;
  } | null;
  badgeName?: string;
  guests?: { name: string; registrationDate?: number }[];
  guestName?: string;
  guestName2?: string;
  eventPayment?: string;
  creditPayment?: string;
  amountRefunded?: string;
  additionalFee?: string;
  paymentMethod?: string;
  paymentMethodOptions?: Array<{ id: string; name: string }>;
  paid?: "paid" | "partial" | "unpaid";
  notes?: string;
  eventId: string;
  registrationId?: string;
  personId?: string;
  filename?: string;
  address?: {
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
  };
  invoiceAddress?:
    | {
        address1?: string;
        address2?: string;
        city?: string;
        state?: string;
        zipCode?: string;
        country?: string;
      }
    | Array<{
        address1?: string;
        address2?: string;
        city?: string;
        state?: string;
        zipCode?: string;
        country?: string;
      }>;
  vatNumber?: string;
  regTerms?: string;
  logoImageDataUrl?: string;
  selectedSpecialEventIds?: string[];
  specialEventGuestNames?: Record<string, string | string[]>;
  registrationDate?: number;
  paymentStatus?: string;
  attendeeType?: string;
  hasPrepaidPaymentInLedger?: boolean;
  isPrepaid?: boolean;
  isHostFirm?: boolean;
  /** Accounting / membership invoices: use pre-built line rows instead of event fees. */
  customLineItems?: { description: string; amountText: string }[];
  /** Replaces "{eventTitle} Registration" above the line-item table. */
  invoiceSectionTitle?: string;
  /** Overrides auto-generated reference in the PDF header. */
  referenceNumber?: string;
  /** Overrides today's date in the PDF header (e.g. "6 July 2026"). */
  invoiceDateDisplay?: string;
  /** Invoice total when using customLineItems. */
  customTotalAmount?: number;
  /** Overrides the "Amount Paid" label for the paid line (custom billing). */
  paidLabel?: string;
  /** Credit balance applied; shown on paid accounting invoices as a line item. */
  creditAppliedDisplay?: number;
  /**
   * Omits the "No refund of the pre-paid fees..." boilerplate appended after
   * the note. Used by accounting invoices, where that event-specific text does
   * not apply. Events/registrations leave this false to keep the disclaimer.
   */
  hidePrepaidRefundNote?: boolean;
}

const formatAddress = (address: InvoicePDFData["address"], country?: string): string => {
  if (!address) {
    return "";
  }
  
  const parts: string[] = [];
  if (address.address1) {
    parts.push(address.address1);
  }
  if (address.address2) {
    parts.push(address.address2);
  }
  
  const countryName = address.country || country || "";
  const isUSOrCanada = countryName === "United States of America" || countryName === "Canada";
  
  if (address.city) {
    if (isUSOrCanada && address.state && address.zip) {
      parts.push(`${address.city}, ${address.state} ${address.zip}`);
    } else if (address.zip) {
      parts.push(`${address.city} ${address.zip}`);
    } else {
      parts.push(address.city);
    }
  }
  
  if (countryName) {
    parts.push(countryName);
  }
  
  return parts.join("\n");
};


export const generateInvoicePDF = async (
  data: InvoicePDFData
): Promise<Blob | null> => {
  const {
    event,
    person,
    firm,
    guests,
    guestName,
    guestName2,
    eventPayment = "0",
    creditPayment = "0",
    additionalFee = "0",
    paid = "unpaid",
    eventId,
    registrationId,
    personId,
    address,
    invoiceAddress,
    vatNumber,
    regTerms,
    logoImageDataUrl,
    selectedSpecialEventIds,
    specialEventGuestNames = {},
    registrationDate,
    paymentStatus,
    attendeeType,
    hasPrepaidPaymentInLedger = false,
    isPrepaid = false,
    isHostFirm = false,
    paymentMethod,
    customLineItems,
    invoiceSectionTitle,
    referenceNumber: referenceNumberOverride,
    invoiceDateDisplay,
    customTotalAmount,
    paidLabel,
    creditAppliedDisplay,
    hidePrepaidRefundNote = false,
  } = data;

  const useCustomBilling =
    Array.isArray(customLineItems) && customLineItems.length > 0;

  if (!useCustomBilling && (!event || !person)) {
    alert("Event and person information are required to generate invoice");
    return null;
  }

  if (useCustomBilling && !person) {
    alert("Billing contact information is required to generate invoice");
    return null;
  }

  const paidAmount = parseFloat(eventPayment || "0");
  const creditAmount = parseFloat(creditPayment || "0");
  const isPaid = paid === "paid";

  let currency: string;
  let finalAmount: number;
  let eventTitle: string;
  let regAmount = 0;
  let applicableGuestFee = 0;
  let program: InvoicePDFData["event"]["program"];
  let additionalFeeValue = 0;

  if (useCustomBilling) {
    currency = event?.fee?.currency || "USD";
    const totalAmount = customTotalAmount ?? 0;
    const amountDue = totalAmount - paidAmount - creditAmount;
    finalAmount = isPaid ? paidAmount : Math.max(0, amountDue);
    eventTitle = event?.title || "Invoice";
  } else {
    const fee = event!.fee;
    currency = fee?.currency || "USD";

    const registrationForFees = {
      guests,
      guestName,
      guestName2,
      registrationDate,
      prepaid: isPrepaid ? true : undefined,
      isHostFirm,
      paymentMethod: isPrepaid ? "prepaid" : paymentMethod,
      paymentStatus,
      attendeeType,
      selectedSpecialEventIds: data.selectedSpecialEventIds ?? [],
    } as Parameters<typeof shouldUseLateFeesForRegistration>[1];

    const waiveGuestFees = shouldWaiveMainGuestFeesForRegistration(
      registrationForFees,
    );
    const numberOfGuests = waiveGuestFees
      ? 0
      : countRegistrationGuests(registrationForFees);
    const guestFee = fee?.guestFee || 0;
    const guestLateFee = fee?.guestLateFee || 0;
    const shouldUseLateFees = shouldUseLateFeesForRegistration(
      { fee } as Parameters<typeof shouldUseLateFeesForRegistration>[0],
      registrationForFees,
    );
    applicableGuestFee = waiveGuestFees
      ? 0
      : shouldUseLateFees && guestLateFee > 0
        ? guestLateFee
        : guestFee;
    const guestTotal = applicableGuestFee * numberOfGuests;
    additionalFeeValue = parseFloat(additionalFee || "0");
    regAmount = resolveInvoiceRegistrationFeeLineAmount(
      { fee } as Parameters<typeof resolveInvoiceRegistrationFeeLineAmount>[0],
      registrationForFees,
      { hasPrepaidPaymentInLedger },
    );
    if (isPrepaid) {
      regAmount = 0;
    }

    let specialEventsTotal = 0;
    program = event!.program;
    const idsToUse = data.selectedSpecialEventIds ?? [];
    if (program?.length && idsToUse.length > 0) {
      const byIdMap = new Map(
        program.filter((p: any) => p.id).map((p: any) => [p.id, p]),
      );
      for (const id of idsToUse) {
        const prog = byIdMap.get(id);
        if (!prog) {
          continue;
        }
        specialEventsTotal += prog.fee ?? 0;
        const guests = getSpecialEventGuestsForProgram(
          specialEventGuestNames,
          id,
        );
        specialEventsTotal += (prog.specialEventGuestFee ?? 0) * guests.length;
      }
    }

    const totalAmount =
      regAmount + guestTotal + additionalFeeValue + specialEventsTotal;
    const amountDue = totalAmount - paidAmount - creditAmount;
    finalAmount = isPaid
      ? isPrepaid && totalAmount <= 0
        ? 0
        : paidAmount
      : Math.max(0, amountDue);
    eventTitle = event!.title || "Event";
  }

  const doc = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "letter",
  });

  let invoiceFontFamily: "NotoSans" | "helvetica" = "helvetica";
  try {
    embedNotoSans(doc, await loadNotoSansBase64());
    invoiceFontFamily = INVOICE_FONT;
  } catch (e) {
    console.warn("Invoice PDF: falling back to Helvetica (Unicode may not render):", e);
  }

  const rightEdge = 550;

  if (logoImageDataUrl) {
    try {
      const logoWidth = 80;
      const logoHeight = (57 / 161) * logoWidth;
      const logoX = 70;
      const logoY = 50;
      doc.addImage(logoImageDataUrl, 'PNG', logoX, logoY, logoWidth, logoHeight);
    } catch (error) {
      console.error('Error adding logo to PDF:', error);
    }
  }

  doc.setDrawColor(0, 0, 0);
  doc.setLineWidth(1);

  doc.line(70, 45, 550, 45);

  const invoiceTypeText = isPaid ? "PAID INVOICE" : "DUE INVOICE";
  doc.setFont(invoiceFontFamily, "bold");
  doc.setFontSize(16);
  doc.setTextColor(29, 106, 174);
  const typeWidth = doc.getTextWidth(invoiceTypeText);
  doc.text(invoiceTypeText, rightEdge - typeWidth - 5, 75);

  doc.setFont(invoiceFontFamily, "normal");
  doc.setFontSize(11);
  doc.setTextColor(0, 0, 0);

  doc.setFont(invoiceFontFamily, "bold");
  doc.setFontSize(11);
  doc.text("BILL TO:", 90, 180);
  doc.text("REMIT TO:", 370, 180);

  doc.setFont(invoiceFontFamily, "normal");
  doc.setFontSize(10);
  
  type InvoiceLine = {
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  };
  let inv: InvoiceLine | undefined;
  if (Array.isArray(invoiceAddress) && invoiceAddress.length > 0) {
    inv = invoiceAddress[0];
  } else if (invoiceAddress && !Array.isArray(invoiceAddress)) {
    inv = invoiceAddress;
  }

  let addressToUse: InvoicePDFData["address"] | undefined;
  if (inv) {
    addressToUse = {
      address1: inv.address1,
      address2: inv.address2,
      city: inv.city,
      state: inv.state,
      zip: inv.zipCode,
      country: inv.country,
    };
  } else {
    addressToUse = address;
  }
  
  const billToAddress = formatAddress(addressToUse, addressToUse?.country);
  const companyName = firm?.name || "";
  const billToLines: string[] = [];
  if (companyName) {
    billToLines.push(companyName);
  }
  if (billToAddress) {
    billToLines.push(...billToAddress.split("\n"));
  }
  if (vatNumber) {
    billToLines.push(`VAT: ${vatNumber}`);
  }
  
  let billToY = 200;
  billToLines.forEach((line) => {
    const lines = doc.splitTextToSize(line, 240);
    lines.forEach((l: string) => {
      doc.text(l, 90, billToY);
      billToY += 12;
    });
  });

  const remitToText = "ATTN: Accounting Department\nWorld Services Group, Inc.\n2777 Allen Pkwy., Suite 622\nHouston, Texas 77019";
  const remitToLines = remitToText.split("\n");
  let remitToY = 200;
  remitToLines.forEach((line) => {
    doc.text(line, 370, remitToY);
    remitToY += 12;
  });

  const currentDate = new Date();
  const defaultDateStr = `${currentDate.getDate()} ${currentDate.toLocaleString("en-US", { month: "long" })} ${currentDate.getFullYear()}`;
  const dateStr = invoiceDateDisplay?.trim() || defaultDateStr;

  const resolvedEventId = eventId || event?.id || "";
  const eventNumericDigits = resolvedEventId.replace(/\D/g, "");
  const eventPrefix = eventNumericDigits.substring(0, 4).padStart(4, "0");

  const userId = personId || registrationId || "";
  const userNumericDigits = userId.replace(/\D/g, "");
  const userPrefix = userNumericDigits.substring(0, 4).padStart(4, "0");

  const referenceNumber =
    referenceNumberOverride?.trim() || `M${eventPrefix}-${userPrefix}`;
  
  doc.setFont(invoiceFontFamily, "normal");
  doc.setFontSize(10);
  const refNumberWidth = doc.getTextWidth(referenceNumber);
  const dateWidth = doc.getTextWidth(dateStr);
  
  const dateX = rightEdge - dateWidth - 5;
  const refX = rightEdge - refNumberWidth - 5;
  
  doc.setFont(invoiceFontFamily, "bold");
  doc.setFontSize(10);
  const dateLabelWidth = doc.getTextWidth("DATE:");
  const refLabelWidth = doc.getTextWidth("REFERENCE #:");
  const labelSpacing = 5;
  
  doc.text("DATE:", dateX - dateLabelWidth - labelSpacing, 110);
  
  doc.setFont(invoiceFontFamily, "bold");
  doc.setFontSize(10);
  doc.text(dateStr, dateX, 110);
  
  doc.setFont(invoiceFontFamily, "bold");
  doc.setFontSize(10);
  doc.text("REFERENCE #:", refX - refLabelWidth - labelSpacing, 130);
  
  doc.setFont(invoiceFontFamily, "bold");
  doc.setFontSize(10);
  doc.text(referenceNumber, refX, 130);

  const lineItems = useCustomBilling
    ? buildCustomInvoicePDFLineItems(customLineItems!, {
        currency,
        paidAmount,
        isPaid,
        paidLabel,
        creditAppliedDisplay,
      })
    : buildInvoicePDFLineItems({
        person: person!,
        regAmount,
        guestNames: normalizeRegistrationGuests({
          guests,
          guestName,
          guestName2,
          registrationDate,
        }).map((g) => g.name),
        applicableGuestFee,
        program,
        selectedSpecialEventIds,
        specialEventGuestNames,
        eventTitle,
        additionalFeeValue,
        creditAmount,
        paidAmount,
        isPaid,
        isPrepaid,
        currency,
      });

  const {
    boxHeight,
    boxBottom,
    totalBarY,
    totalTextY,
    itemsStartY,
    layoutShift,
  } = calculateInvoiceBoxLayout(doc, invoiceFontFamily, lineItems);

  const { positive: currencySymbol } = getCurrencySymbols(currency);

  doc.setFillColor(240, 248, 255);
  doc.rect(
    INVOICE_BOX.x,
    INVOICE_BOX.top,
    INVOICE_BOX.width,
    INVOICE_BOX.headerHeight,
    "F",
  );

  doc.setFillColor(217, 217, 217);
  doc.rect(
    INVOICE_BOX.x,
    totalBarY,
    INVOICE_BOX.width,
    INVOICE_BOX.totalRowHeight,
    "F",
  );

  doc.setDrawColor(0, 0, 0);
  doc.rect(INVOICE_BOX.x, INVOICE_BOX.top, INVOICE_BOX.width, boxHeight);
  doc.line(
    INVOICE_BOX.amountDividerX,
    INVOICE_BOX.top,
    INVOICE_BOX.amountDividerX,
    boxBottom,
  );

  doc.setFont(invoiceFontFamily, "normal");
  doc.setFontSize(11);
  doc.setTextColor(0, 0, 0);
  doc.text("Description", 230, INVOICE_BOX.top + 13);
  doc.text("Amount", 480, INVOICE_BOX.top + 13);

  doc.setFont(invoiceFontFamily, "bold");
  doc.setFontSize(10);
  const eventTitleText =
    invoiceSectionTitle?.trim() || `${eventTitle} Registration`;
  const eventTitleWidth = doc.getTextWidth(eventTitleText);
  doc.text(
    eventTitleText,
    INVOICE_BOX.amountDividerX - eventTitleWidth - 5,
    INVOICE_BOX.top + INVOICE_BOX.eventTitleOffset,
  );

  doc.setFont(invoiceFontFamily, "normal");
  doc.setFontSize(10);

  let descriptionY = itemsStartY;
  for (const item of lineItems) {
    descriptionY = drawDescriptionAmountRow(
      doc,
      invoiceFontFamily,
      item.description,
      item.amountText,
      descriptionY,
    );
  }

  doc.setFont(invoiceFontFamily, "bold");
  doc.setFontSize(11);
  const totalTypeText = `TOTAL ${isPaid ? "PAID" : "DUE"}`;
  doc.text(totalTypeText, 370, totalTextY);
  doc.text(
    `${currencySymbol}${Math.max(0, finalAmount).toFixed(2)}`,
    AMOUNT_X,
    totalTextY,
  );

  const footerNeedsNewPage = layoutShift + 430 + 280 > PAGE_HEIGHT;
  if (footerNeedsNewPage) {
    doc.addPage();
  }
  const footerYOffset = footerNeedsNewPage ? 70 - 430 : layoutShift;
  const footerY = (y: number) => y + footerYOffset;

  if (isPaid && regTerms && regTerms.trim()) {
    doc.setDrawColor(0, 0, 0);
    doc.rect(INVOICE_BOX.x, footerY(430), INVOICE_BOX.width, 60);
    doc.setFont(invoiceFontFamily, "normal");
    doc.setFontSize(10);
    doc.setTextColor(128, 0, 0);
    const noteText = `NOTE: ${regTerms.replace(/\n/g, " ")}`;
    const noteLines = doc.splitTextToSize(noteText, 470);
    let noteY = footerY(445);
    noteLines.forEach((line: string) => {
      doc.text(line, 80, noteY);
      noteY += LINE_HEIGHT;
    });
  } else if (!isPaid) {
    doc.setDrawColor(0, 0, 0);
    doc.rect(INVOICE_BOX.x, footerY(445), 240, 157);
    doc.rect(310, footerY(445), 240, 157);
    doc.line(310, footerY(445), 310, footerY(602));
    const creditCardContainerHeight = regTerms && regTerms.trim() ? 178 : 94;
    doc.rect(
      INVOICE_BOX.x,
      footerY(602),
      INVOICE_BOX.width,
      creditCardContainerHeight,
    );

    doc.setFont(invoiceFontFamily, "bold");
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text("Payment Options: ", INVOICE_BOX.x, footerY(434));
    doc.text("CHECK:", 77, footerY(458));
    doc.text("WIRE TRANSFER:", 320, footerY(458));
    doc.text("CREDIT CARD:", 77, footerY(616));

    doc.setFont(invoiceFontFamily, "italic");
    doc.setFontSize(7);
    doc.text("Drawn on U.S. bank payable in U.S. Dollars", 77, footerY(471));
    doc.text("Please provide your wire transfer reference", 320, footerY(471));

    doc.setFont(invoiceFontFamily, "normal");
    doc.setFontSize(9);
    const checkPayableText = "Make Checks Payable to:\n\nWorld Services Group, Inc.\n2777 Allen Pkwy., Suite #622\nHouston, Texas 77019";
    const checkLines = checkPayableText.split("\n");
    let checkY = footerY(491);
    checkLines.forEach((line) => {
      doc.text(line, 77, checkY);
      checkY += LINE_HEIGHT;
    });

    const refY = checkY + LINE_HEIGHT;
    doc.text(`REF: ${referenceNumber}`, 77, refY);

    const wireTransferText = "Bank of America\n901 Main Street\nDallas, Texas 75202\nUSA\n\nABA NO. 0260-0959-3\nCredit to:\nWorld Services Group, Inc.\nAccount No: 005740190806\nSWIFT: BOFAUS3N";
    const wireLines = wireTransferText.split("\n");
    let wireY = footerY(491);
    wireLines.forEach((line) => {
      doc.text(line, 320, wireY);
      wireY += LINE_HEIGHT;
    });

    doc.setFont(invoiceFontFamily, "normal");
    doc.setFontSize(9);

    const visaLabelText = "VISA  MC  AMEX  Other:";
    doc.text(visaLabelText, 77, footerY(636));
    const visaLabelWidth = doc.getTextWidth(visaLabelText);
    doc.line(
      77 + visaLabelWidth + 2,
      footerY(638),
      77 + visaLabelWidth + 2 + 200,
      footerY(638),
    );

    const cardNumberLabelText = "Card Number:";
    doc.text(cardNumberLabelText, 77, footerY(651));
    const cardNumberLabelWidth = doc.getTextWidth(cardNumberLabelText);
    const cardNumberUnderlineWidth = doc.getTextWidth("0") * 20;
    doc.line(
      77 + cardNumberLabelWidth + 2,
      footerY(653),
      77 + cardNumberLabelWidth + 2 + cardNumberUnderlineWidth,
      footerY(653),
    );

    const expLabelText = "Exp.:";
    doc.text(
      expLabelText,
      77 + cardNumberLabelWidth + 10 + cardNumberUnderlineWidth,
      footerY(651),
    );
    const expLabelWidth = doc.getTextWidth(expLabelText);
    const expUnderlineWidth = doc.getTextWidth("0") * 8;
    doc.line(
      77 + cardNumberLabelWidth + 10 + cardNumberUnderlineWidth + expLabelWidth + 2,
      footerY(653),
      77 + cardNumberLabelWidth + 10 + cardNumberUnderlineWidth + expLabelWidth + 2 + expUnderlineWidth,
      footerY(653),
    );

    const cardHolderLabelText = "Card Holder Name:";
    doc.text(cardHolderLabelText, 77, footerY(666));
    const cardHolderLabelWidth = doc.getTextWidth(cardHolderLabelText);
    const cardHolderUnderlineWidth = doc.getTextWidth("M") * 30;
    doc.line(
      77 + cardHolderLabelWidth + 2,
      footerY(668),
      77 + cardHolderLabelWidth + 2 + cardHolderUnderlineWidth,
      footerY(668),
    );

    const billingLabelText = "Billing Address:";
    doc.text(billingLabelText, 77, footerY(681));
    const billingLabelWidth = doc.getTextWidth(billingLabelText);
    const billingUnderlineWidth = doc.getTextWidth("M") * 40;
    doc.line(
      77 + billingLabelWidth + 2,
      footerY(683),
      77 + billingLabelWidth + 2 + billingUnderlineWidth,
      footerY(683),
    );

    doc.setDrawColor(0, 0, 0);
    doc.line(70, footerY(696), 550, footerY(696));

    if (regTerms && regTerms.trim()) {
      doc.setFont(invoiceFontFamily, "normal");
      doc.setFontSize(9);
      doc.setTextColor(128, 0, 0);
      const termsText = hidePrepaidRefundNote
        ? `NOTE: ${regTerms.replace(/\n/g, " ")}`
        : `NOTE: ${regTerms.replace(/\n/g, " ")}\n\nNo refund of the pre-paid fees. If the member does not attend, the fees cannot be rolled over and the fees will not be refunded.`;
      const termsLines = doc.splitTextToSize(termsText, 460);
      let termsY = footerY(708);
      termsLines.forEach((line: string) => {
        doc.text(line, 75, termsY);
        termsY += LINE_HEIGHT;
      });
    }
  }

  const pdfBlob = doc.output("blob");
  return pdfBlob;
};

export const openInvoicePDF = async (pdfBlobOrUrl: Blob | string, filename?: string): Promise<void> => {
  let url: string;
  let shouldRevokeUrl = false;

  if (typeof pdfBlobOrUrl === 'string') {
    const isBlobStorageUrl = pdfBlobOrUrl.includes('blob.core.windows.net');
    
    if (isBlobStorageUrl) {
      try {
        const response = await fetch(pdfBlobOrUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
        }
        const blobData = await response.blob();
        const blob = new Blob([blobData], { type: 'application/pdf' });
        url = window.URL.createObjectURL(blob);
        shouldRevokeUrl = true;
      } catch (error) {
        console.error('Error fetching PDF from blob storage:', error);
        url = pdfBlobOrUrl;
      }
    } else {
      url = pdfBlobOrUrl;
    }
  } else {
    url = window.URL.createObjectURL(pdfBlobOrUrl);
    shouldRevokeUrl = true;
  }

  try {
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    
    if (!newWindow) {
      const link = document.createElement("a");
      link.href = url;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    if (shouldRevokeUrl) {
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 1000);
    }
  } catch (error) {
    console.error('Error opening invoice PDF:', error);
    if (shouldRevokeUrl) {
      window.URL.revokeObjectURL(url);
    }
    throw error;
  }
};

/**
 * Downloads a PDF file instead of opening it in a new tab.
 * Can accept either a Blob or a URL string.
 * For blob storage URLs, fetches the blob and creates a local object URL to download.
 * 
 * @param pdfBlobOrUrl - The PDF as a Blob or a URL string
 * @param filename - Filename for the download
 */
export const downloadInvoicePDF = async (pdfBlobOrUrl: Blob | string, filename: string): Promise<void> => {
  let url: string;
  let shouldRevokeUrl = false;
  let blob: Blob;

  if (typeof pdfBlobOrUrl === 'string') {
    const isBlobStorageUrl = pdfBlobOrUrl.includes('blob.core.windows.net');
    
    if (isBlobStorageUrl) {
      try {
        const response = await fetch(pdfBlobOrUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
        }
        blob = await response.blob();
        url = window.URL.createObjectURL(blob);
        shouldRevokeUrl = true;
      } catch (error) {
        console.error('Error fetching PDF from blob storage:', error);
        throw error;
      }
      } else {
      try {
        const response = await fetch(pdfBlobOrUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
        }
        blob = await response.blob();
        url = window.URL.createObjectURL(blob);
        shouldRevokeUrl = true;
      } catch (error) {
        console.error('Error fetching PDF:', error);
        throw error;
      }
    }
  } else {
    blob = pdfBlobOrUrl;
    url = window.URL.createObjectURL(blob);
    shouldRevokeUrl = true;
  }

  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename || "invoice.pdf";
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    console.error('Error downloading invoice PDF:', error);
    throw error;
  } finally {
    if (shouldRevokeUrl) {
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
    }
  }
};

export const convertLogoSvgToImage = async (): Promise<string | null> => {
  try {
    const logoSvgModule = await import('../assets/images&icons/logo-wsg.svg');
    const logoSvgUrl = logoSvgModule.default || logoSvgModule;
    
    if (!logoSvgUrl || typeof logoSvgUrl !== 'string') {
      return null;
    }

    const response = await fetch(logoSvgUrl);
    const svgText = await response.text();
    
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      return null;
    }

    const img = new Image();
    const svgBlob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    
    return new Promise((resolve) => {
      img.onload = () => {
        canvas.width = 161;
        canvas.height = 57;
        ctx.drawImage(img, 0, 0);
        const dataUrl = canvas.toDataURL('image/png');
        URL.revokeObjectURL(url);
        resolve(dataUrl);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(null);
      };
      img.src = url;
    });
  } catch (error) {
    console.error('Error converting logo SVG to image:', error);
    return null;
  }
};

export const generateInvoicePDFWithLogo = async (data: InvoicePDFData): Promise<Blob | null> => {
  if (!data.logoImageDataUrl) {
    const logoDataUrl = await convertLogoSvgToImage();
    if (logoDataUrl) {
      data.logoImageDataUrl = logoDataUrl;
    }
  }
  
  return await generateInvoicePDF(data);
};

export const saveInvoicePDFToBlob = async (
  pdfBlob: Blob,
  filename: string,
  personId: string,
  eventId: string
): Promise<string | undefined> => {
  try {
    const reader = new FileReader();
    return new Promise<string | undefined>((resolve, reject) => {
      reader.onload = async () => {
        if (reader.result && typeof reader.result === "string") {
          const base64FileData = reader.result.split(",")[1];
          const extension = "pdf";
          const fileNameWithoutExtension = filename.replace(/\.pdf$/i, "");

          try {
            const Axios = (await import("./axios")).default;
            const axios = new Axios();

            const file = {
              fileData: base64FileData,
              fileName: fileNameWithoutExtension.trim(),
              extension: extension,
              path: `user/profile-pictures/${personId}/EventInvoices/${eventId}`,
            };

            const response = await axios.Post("/upload-files", file);

            if (response.status === 200) {
              const link = response.data.image as string;
              const storagePath = `user/profile-pictures/${personId}/EventInvoices/${eventId}/${fileNameWithoutExtension}.${extension}`;
              
              if (link && link.startsWith("http")) {
                resolve(link);
              } else {
                resolve(storagePath);
              }
            } else {
              console.error(
                "Error uploading PDF. Status:",
                response.status,
                "Data:",
                response.data
              );
              resolve(undefined);
            }
          } catch (error) {
            console.error("Error uploading PDF:", error);
            resolve(undefined);
          }
        }
      };
      reader.onerror = () => {
        console.error("Error reading PDF blob");
        resolve(undefined);
      };
      reader.readAsDataURL(pdfBlob);
    });
  } catch (error) {
    console.error("Error handling PDF upload:", error);
    return undefined;
  }
};
